import React, { useEffect, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { Breadcrumbs } from '../../../components'
import UploadFormModal from '../../../forms/UploadFormModal'
import { selectAllAudios } from '../../../redux/features/audio/audioSelector'
import { deleteAudioThunk } from '../../../redux/features/audio/audioThunk'
import { ViewContentModal } from '../../../components/ViewContentModal'
import { ContentTable } from '../../../components/ContentTable'
import { ContentFilterBar } from '../../../components/ContentFilterBar'
import { BulkActionsBar } from '../../../components/BulkActionsBar'

interface DataType {
  key: string
  id: string
  title: string
  category: string
  description: string
  thumbnail: string
  url: string
  likes?: number
  views?: number
  createdAt?: string
}

const AudioPage = () => {
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [isUploadFormModalVisible, setIsUploadFormModalVisible] = useState(false)
  const [viewContent, setViewContent] = useState<DataType | null>(null)
  const [editData, setEditData] = useState<any>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<DataType[]>([])

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined)

  const audios = useSelector(selectAllAudios)
  const dispatch = useDispatch<any>()

  // Transform audio data
  const transformedAudioData: DataType[] = useMemo(() => {
    return audios.map((audio) => ({
      key: audio?.id,
      id: audio?.id,
      title: audio?.title || '',
      category: audio?.category || '',
      description: audio?.description || '',
      thumbnail: audio?.thumbnail || '',
      url: audio?.url || '',
      likes: audio?.likes || 0,
      views: (audio as any)?.views || 0,
      createdAt: (audio as any)?.createdAt,
    }))
  }, [audios])

  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = transformedAudioData
      .map((a) => a.category)
      .filter((c) => c && c.trim() !== '')
    return [...new Set(cats)]
  }, [transformedAudioData])

  // Filtered data
  const filteredData = useMemo(() => {
    let result = transformedAudioData

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      )
    }

    if (categoryFilter) {
      result = result.filter((item) => item.category === categoryFilter)
    }

    return result
  }, [transformedAudioData, searchQuery, categoryFilter])

  const handleView = (record: DataType) => {
    setViewContent(record)
    setIsViewModalVisible(true)
  }

  const handleEdit = (record: DataType) => {
    setEditData({
      id: record.id,
      title: record.title,
      description: record.description,
      category: record.category,
      thumbnail: record.thumbnail,
      url: record.url,
    })
    setIsUploadFormModalVisible(true)
  }

  const handleDelete = (record: DataType) => {
    dispatch(deleteAudioThunk(record.id))
    message.success('Audio deleted successfully')
  }

  const handleBulkDelete = () => {
    selectedRows.forEach((record) => {
      dispatch(deleteAudioThunk(record.id))
    })
    message.success(`${selectedRows.length} audio files deleted successfully`)
    setSelectedRowKeys([])
    setSelectedRows([])
  }

  const handleSelectionChange = (keys: React.Key[], records: DataType[]) => {
    setSelectedRowKeys(keys)
    setSelectedRows(records)
  }

  const handleViewModalClose = () => {
    setIsViewModalVisible(false)
    setViewContent(null)
  }

  const handleSwitchToEdit = () => {
    if (viewContent) {
      handleEdit(viewContent)
      handleViewModalClose()
    }
  }

  const showUploadFormModal = () => {
    setEditData(null)
    setIsUploadFormModalVisible(true)
  }

  const handleCancelUploadFormModal = () => {
    setIsUploadFormModalVisible(false)
    setEditData(null)
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setCategoryFilter(undefined)
  }

  return (
    <div>
      <Breadcrumbs />

      {/* Upload Button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          marginBottom: '16px',
          background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
          borderRadius: '10px',
          border: '1px solid #d3adf7',
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: '#722ed1' }}>Audio Files</h3>
          <span style={{ color: '#666' }}>{filteredData.length} audio files</span>
        </div>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={showUploadFormModal}
          style={{ background: '#722ed1', borderColor: '#722ed1' }}
        >
          Upload Audio
        </Button>
      </div>

      {/* Filter Bar */}
      <ContentFilterBar
        onSearch={setSearchQuery}
        onCategoryChange={setCategoryFilter}
        onClear={handleClearFilters}
        searchValue={searchQuery}
        categoryValue={categoryFilter}
        categories={categories}
        placeholder="Search audio files..."
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedRowKeys.length}
        onBulkDelete={handleBulkDelete}
        onClearSelection={() => {
          setSelectedRowKeys([])
          setSelectedRows([])
        }}
        itemType="audio file"
      />

      {/* Content Table */}
      <ContentTable
        dataSource={filteredData}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={handleSelectionChange}
        type="audio"
      />

      {/* View Modal */}
      {viewContent && (
        <ViewContentModal
          isVisible={isViewModalVisible}
          onClose={handleViewModalClose}
          onEdit={handleSwitchToEdit}
          type="audio"
          content={viewContent}
        />
      )}

      {/* Upload/Edit Modal */}
      <UploadFormModal
        visible={isUploadFormModalVisible}
        onClose={handleCancelUploadFormModal}
        label="audio"
        editData={editData}
      />
    </div>
  )
}

export { AudioPage }
